import {
    BulkRequest,
    BulkRequestStatus,
    CustomerType,
    Field,
    FrontEndSignature,
    Package,
    Request,
    RequestStatus,
    SigStatus,
    SignData,
    User,
    Webhook,
    Plan,
    Seat,
    SeatStatus,
    Team,
    RequestSource,
    AppSubsCount,
    MonthlyAppSubsCount,
    Brand,
    RecipientTag,
    Recipient,
    EmailTextTemplate,
    EmailTextTemplateType,
    RequestDocumentInfo,
    APP as APPType,
} from '@/types/types';
import { api } from './client';
import axios from 'axios';
import { wait } from '@/utils/wait';
import { Dispatch, SetStateAction } from 'react';
import saveAs from 'file-saver';
import { API_BASE_URL, CLIENT_VERSION } from '@/constants';
import {
    appendSignedSuffix,
    extractFilenameForCertificate,
    extractFileNameFromHeader,
    toDocName,
} from '@/utils/text';
import * as Sentry from '@sentry/react';

export const createUser = ({
    name,
    isApiDev = false,
    campaign,
    appsumoCode,
    partnerstackXid,
    gclid,
    liFatId,
    adConsent,
}: {
    name: string;
    isApiDev?: boolean;
    campaign?: 'appsumo';
    appsumoCode?: string;
    partnerstackXid?: string;
    gclid?: string;
    liFatId?: string;
    adConsent: boolean;
}) => {
    // user data is decoded by backend from the user token
    return api.post<User>('/users', {
        name,
        isApiDev,
        campaign,
        appsumoCode,
        partnerstackXid,
        gclid,
        liFatId,
        adConsent,
    });
};

export const getUser = () => {
    // user id is decoded by backend from the user token
    return api.get<User>(`/users`);
};

export const downloadOpenApiSchema = async () => {
    const response = await api.get<Blob>('/openapi.json', {
        responseType: 'blob',
    });
    const fileName = extractFileNameFromHeader(
        response.headers['content-disposition']
    );

    saveAs(response.data, fileName);
};

export const updateUser = (user: Partial<User>) => {
    // user id is decoded by backend from the user token
    return api.put<User>('/users', user);
};

export const resendEmailVerification = () => {
    return api.post<{ message: string }>('/users/resendEmailVerification');
};

export const requestEmailChange = (newEmail: string) => {
    return api.post('/users/request-email-change', {
        newEmail,
    });
};

export const confirmEmailChange = (token: string) => {
    return api.post<{ message: string; newEmail: string }>(
        '/users/confirm-email-change',
        undefined,
        { params: { token } }
    );
};

export const deleteUser = () => {
    return api.delete(`/users`);
};

export interface UpdateTemplateParams {
    // Own upload or merge result, without the user folder
    mainDocumentName?: string;
    // Ordered extra documents, except the locked ones of the template a request is created from
    additionalDocumentIds: string[];
    recipients: string[];
    signInOrder: boolean;
    fields: Field[];
    emailMessage: string;
    emailSubject: string;
    tags: string[];
}

export interface CreateRequestParams extends UpdateTemplateParams {
    templateId?: string;
    asTemplate?: boolean;
}

export const createRequest = (requestData: CreateRequestParams) => {
    return api.post<Partial<Request>>('/requests', requestData);
};

export const updateTemplate = (id: string, data: UpdateTemplateParams) => {
    return api.put<Request>(`/templates/${id}`, data);
};

export const getSignDataNoAuth = (requestId: string, token: string) => {
    return api.get<SignData>(`requests/${requestId}/sign/noauth`, {
        params: {
            token,
            multiDoc: true,
        },
    });
};

export const getSignData = (requestId: string, recipientId: string) => {
    return api.get<SignData>(`requests/${requestId}/sign`, {
        params: {
            recipientId,
            multiDoc: true,
        },
    });
};

export const signNoAuth = (
    requestId: string,
    sigs: FrontEndSignature[],
    token: string,
    sessionId?: string,
    docHash?: string
) => {
    return api.put<any>(
        `requests/${requestId}/sign/noauth`,
        { sigs, docHash },
        {
            params: {
                token,
            },
            headers: sessionId ? { 'X-Session-Id': sessionId } : undefined,
        }
    );
};

export const sign = ({
    requestId,
    sigs,
    recipientId,
    sessionId,
    docHash,
}: {
    requestId: string;
    sigs: FrontEndSignature[];
    recipientId: string;
    sessionId?: string;
    docHash?: string;
}) => {
    return api.put<any>(
        `requests/${requestId}/sign`,
        {
            sigs,
            recipientId,
            docHash,
        },
        {
            headers: sessionId ? { 'X-Session-Id': sessionId } : undefined,
        }
    );
};

export const getSentRequests = ({
    cursor,
    limit = 10,
    from,
    to,
    searchQuery,
    status,
    senders,
    recipients,
    tags,
}: {
    cursor?: string;
    limit?: number;
    from?: Date | null;
    to?: Date | null;
    searchQuery?: string | null;
    status?: RequestStatus[];
    senders?: string[];
    recipients?: string[];
    tags?: string[];
}) => {
    return api.get<Request[]>('/requests/sent', {
        params: {
            cursor,
            limit,
            from,
            to,
            searchQuery: searchQuery || null,
            status: status?.length ? status.join(',') : null,
            senders: senders?.length ? senders.join(',') : null,
            recipients: recipients?.length ? recipients.join(',') : null,
            tags: tags?.length ? tags.join(',') : null,
        },
    });
};

export const getTemplates = ({
    cursor,
    limit = 10,
}: {
    cursor?: string;
    limit?: number;
}) => {
    return api.get<Request[]>('/requests/sent', {
        params: {
            cursor,
            limit,
            status: RequestStatus.TEMPLATE,
        },
    });
};

export const getReceivedRequests = ({
    cursor,
    limit = 10,
}: {
    cursor?: string;
    limit?: number;
}) => {
    return api.get<Request[]>('/requests/received', {
        params: {
            cursor,
            limit,
        },
    });
};

export const getTempSignedDocUrl = (
    requestId: string,
    { isBulk }: { isBulk?: boolean } = {}
) => {
    return api.get<{ docUrl: string }>(`/request/${requestId}/docurl`, {
        params: { isBulk },
    });
};

export const getRequestStatus = (requestId: string) => {
    return api.get<{ status: RequestStatus }>(`/request/${requestId}/status`);
};

export const getRequestById = (requestId: string) => {
    return api.get<Request>(`/request/${requestId}`);
};

export const getReceivedRequestById = (requestId: string) => {
    return api.get<Request>(`/request/${requestId}/received`);
};

export const getRecipientSigStatus = (recipientId: string) => {
    return api.get<{ sigStatus: SigStatus }>(
        `/recipients/${recipientId}/status`
    );
};

// export const waitForRequestCompletedStatus = (
//     requestId: string
// ): Promise<void> => {
//     const MAX_RETRIES = 100;
//     const MAX_DURATION = 10 * 60 * 1000; // 10 min

//     let retriesCount = 0;
//     let startTime = Date.now();

//     let waitTime = 2000;

//     return new Promise((resolve, reject) => {
//         const getStatus = async () => {
//             try {
//                 const duration = Date.now() - startTime;
//                 retriesCount++;

//                 const { data } = await getRequestStatus(requestId);

//                 if (retriesCount > MAX_RETRIES || duration > MAX_DURATION) {
//                     reject();
//                     return;
//                 }

//                 if (data.status === RequestStatus.COMPLETED) {
//                     resolve();
//                     return;
//                 }

//                 await wait(waitTime);
//                 waitTime += 1000;
//                 getStatus();
//             } catch (error) {
//                 reject(error);
//             }
//         };
//         getStatus();
//     });
// };

export const cancelRequest = (requestId: string) => {
    return api.patch(`/requests/${requestId}`);
};

export const deleteRequest = (requestId: string) => {
    return api.delete(`/requests/${requestId}`);
};

export const convertToPdf = (fileName: string) => {
    return api.put(`/conversions/${fileName}`);
};

export const mergeFiles = (
    source: { fileName_1: string } | { templateId: string },
    fileName_2: string
) => {
    return api.put<{ mergedDocUrl: string }>(`/files/merge/v2`, {
        ...source,
        fileName_2,
    });
};

export const getIp = () => {
    return axios.get<{ ip: string }>('https://api.ipify.org?format=json', {
        timeout: 2500,
    });
};

export const verifyEmail = (token: string) => {
    return api.put(
        `/users/verifyEmail`,
        {},
        {
            params: {
                token,
            },
        }
    );
};

//super apis

export const getRequests = ({
    cursor,
    limit = 10,
    from,
    to,
    searchQuery,
    status,
    recipients,
    tags,
    source,
    app,
}: any) => {
    return api.get<Request[]>('/super/requests', {
        params: {
            cursor,
            limit,
            from,
            to,
            searchQuery,
            status: status?.length ? status.join(',') : null,
            recipients: recipients?.length ? recipients.join(',') : null,
            tags: tags?.length ? tags.join(',') : null,
            source,
            app,
        },
    });
};

export const getTempSignedDocUrl_super = (requestId: string) => {
    return api.get<{ docUrl: string }>(`/super/docurl/${requestId}`);
};

export interface GetStatsResponse {
    users: number;
    deletedUsers: number;
    senders: number;
    requests: number;
    inProgress: number;
    completed: number;
    canceled: number;
    bulkCount: number;
    bulkRequestsCount: number;
    apiRequestsCount: number;
    recipientsPerRequestAvg: string;
    fieldsPerRecipientAvg: string;
    apiDevAccountsCount: number;
    apiDevRequestsCount: number;
}

export const super_getStats = (app: APPType) => {
    return api.get<GetStatsResponse>(`/super/stats`, { params: { app } });
};

export interface WeeklyResponse {
    total: number;
    weekly: Weekly[];
}

export interface MonthlyResponse {
    total: number;
    monthly: Monthly[];
}

export interface Weekly {
    count: number;
    week: number;
}

export interface Monthly {
    count: number;
    month: number;
    year: number;
}

export const super_getWeeklyRequestsCount = ({
    from,
    source,
    app,
}: {
    from?: Date;
    source: RequestSource;
    app: APPType;
}) => {
    return api.get<WeeklyResponse>(`/super/weeklyRequestsCount`, {
        params: { from, source, app },
    });
};

export const super_getMonthlyRequestsCount = ({
    from,
    source,
    app,
}: {
    from?: Date;
    source: RequestSource;
    app: APPType;
}) => {
    return api.get<MonthlyResponse>(`/super/monthlyRequestsCount`, {
        params: { from, source, app },
    });
};

export const super_getWeeklyNewUsersCount = (app: APPType, from?: Date) => {
    return api.get<{ weekly: Weekly[] }>(`/super/weeklyNewUsersCount`, {
        params: { from, app },
    });
};

export const super_getMonthlyNewUsersCount = (app: APPType, from?: Date) => {
    return api.get<{ monthly: Monthly[] }>(`/super/monthlyNewUsersCount`, {
        params: { from, app },
    });
};

export interface MonthlyDistinctSendersData {
    count: number;
    emailDomains: string[];
    month: number;
    year: number;
}

export interface MonthlyDistinctSendersResponse {
    monthly: MonthlyDistinctSendersData[];
}

export const super_getMonthlyDistinctSenders = ({
    from,
    source,
    app,
}: {
    from?: Date;
    source: RequestSource;
    app: APPType;
}) => {
    return api.get<MonthlyDistinctSendersResponse>(
        `/super/monthlyDistinctSenders`,
        {
            params: { from, source, app },
        }
    );
};

export const super_getWeeklySubscriptions = (from: Date, app: APPType) => {
    return api.get<{ subsCount: AppSubsCount[] }>(`/super/subsCount`, {
        params: { from, app },
    });
};

export const super_getMonthlySubscriptions = (from: Date, app: APPType) => {
    return api.get<{ subsCount: MonthlyAppSubsCount[] }>(
        `/super/monthlySubsCount`,
        {
            params: { from, app },
        }
    );
};

export type DailyResponse = Daily[];
export interface Daily {
    count: number;
    day: string;
}

export const super_getDailyRequestsCount = (
    from: Date,
    source: RequestSource,
    app: APPType
) => {
    return api.get<DailyResponse>(`/super/dailyRequestsCount`, {
        params: { from, source, app },
    });
};

export interface DailyNewUsersStats {
    users: UserStats[];
    count: number;
    noRequestsCount: number;
    selfOnlyCount: number;
    sentRequestsCount: number;
    date: string;
}

export interface UserStats {
    name: string;
    email: string;
    requests: number;
}

export const super_getDailyNewUsersCount = (app: APPType, from?: Date) => {
    return api.get<DailyNewUsersStats[]>(`/super/dailyNewUsersCount`, {
        params: { from, app },
    });
};

export interface UserRequestsCount {
    id: string;
    createdAt: string;
    name: string;
    email: string;
    emailVerified: boolean;
    plan: string;
    converted: boolean;
    requestsCount: number;
    completedCount: number;
    inProgressCount: number;
    templateCount: number;
    canceledCount: number;
    deletedCount: number;
    lastRequestDate: string;
    onlySelfSigned: boolean;
    isRecipient: boolean;
    // uniqueRecipients: string[];
    uniqueRecipientsCount: number;
}

export const super_getUsersRequestsCount = (app: APPType) => {
    return api.get<UserRequestsCount[]>(`/super/usersRequestsCount`, {
        params: { app },
    });
};

export interface OfflineUser {
    id: string;
    email: string;
    name: string | null;
    createdAt: string;
    paidUntil: string | null;
    plan: string;
}

export const super_getOfflineUsers = (app: APPType) => {
    return api.get<OfflineUser[]>(`/super/offlineUsers`, { params: { app } });
};

export const super_createApiToken = (usersEmails: string[]) => {
    return api.post(`/super/apiPackages`, { usersEmails });
};

export interface PackageResponse extends Package {
    user: Partial<User & { createdAt: string }>;
    numOfRequests: number;
    consumption: number;
    consumptionPrecentage: string;
    isApiDev: boolean;
}

export const super_getApiPackages = (app: APPType) => {
    return api.get<PackageResponse[]>(`/super/apiPackages`, {
        params: { app },
    });
};

export const script_migrateRecipientsV2 = () => {
    return api.put(`/scripts/migrateRecipientsV2`);
};

export const script_migratePackages = () => {
    return api.put(`/scripts/migratePackages`);
};

export const script_firstScript = () => {
    return api.put(`/scripts/firstScript`, undefined, { timeout: 10 * 60000 });
};

export const super_cleanApiDevRequests = () => {
    return api.get('/super/cleanApiDev');
};

export const super_deleteOldShortUrls = () => {
    return api.delete<{ deletedCount: number }>('/super/shortUrls/old');
};

export const super_downloadDocument = async (requestId: string) => {
    try {
        const response = await api.get(
            `/super/requests/${requestId}/document`,
            {
                responseType: 'blob',
            }
        );

        const contentDisposition = response.headers['content-disposition'];

        let filename = 'document.pdf';

        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch && filenameMatch[1]) {
                filename = toDocName(filenameMatch[1]);
            }
        }

        const blob = new Blob([response.data], { type: 'application/pdf' });
        saveAs(blob, filename);

        return response;
    } catch (error) {
        throw error;
    }
};

export const super_downloadLawEnforcementReport = async (requestId: string) => {
    const { blob, contentDisposition } = await getDownloadBlob(
        `/super/requests/${requestId}/law-enforcement-report`,
        { requestId, method: 'super_downloadLawEnforcementReport' }
    );
    const encoded = contentDisposition?.match(
        /filename\*=UTF-8''([^;]+)/i
    )?.[1];
    const plain = contentDisposition?.match(/filename="([^"]+)"/i)?.[1];
    saveAs(
        blob,
        encoded ? decodeURIComponent(encoded) : (plain ?? 'Raport.pdf')
    );
};

export const getUserTags = () => {
    return api.get<string[]>(`/users/tags`);
};

export const getUserSignature = () => {
    return api.get<{ signatureBase64: string }>(`/users/signature`);
};

export interface FiltersData {
    tags: string[];
    recipients: string[];
    senders: string[];
}

export const getRequestsFilters = () => {
    return api.get<FiltersData>(`/requests/filters`);
};

export const searchRecipients = (search: string) => {
    return api.get<{ recipients: string[] }>(`/requests/recipients/search`, {
        params: { search },
    });
};

export const getTemplateById = (id: string) => {
    return api.get<Request>(`/templates/${id}`);
};

export const waitForCustomerType = (
    customerType: CustomerType
): Promise<User> => {
    const MAX_RETRIES = 40;
    const MAX_DURATION = 40 * 1000; // 40s

    let retriesCount = 0;
    let startTime = Date.now();

    return new Promise((resolve, reject) => {
        const getStatus = async () => {
            try {
                const duration = Date.now() - startTime;
                retriesCount++;

                const { data } = await getUser();

                if (retriesCount > MAX_RETRIES || duration > MAX_DURATION) {
                    reject(new Error('Plan activation error.'));
                    return;
                }

                if (data.customerType === customerType) {
                    resolve(data);
                    return;
                }

                await wait(1000);
                getStatus();
            } catch (error) {
                reject(error);
            }
        };
        getStatus();
    });
};

export const syncSubscriptionState = (setUser: any) => {
    const MAX_RETRIES = 4;
    const WAIT_TIME = 1000; // 1s
    const MAX_DURATION = MAX_RETRIES * WAIT_TIME + 3000;

    let retriesCount = 0;
    let startTime = Date.now();

    const getStatus = async () => {
        try {
            const duration = Date.now() - startTime;
            retriesCount++;

            const { data } = await getUser();

            if (retriesCount > MAX_RETRIES || duration > MAX_DURATION) {
                return;
            }

            setUser((prev: User): User => {
                return {
                    ...prev,
                    customerType: data.customerType,
                    paidUntil: data.paidUntil,
                    plan: data.plan,
                    numOfSeats: data.numOfSeats,
                };
            });

            await wait(WAIT_TIME);
            getStatus();
        } catch (error) {
            return;
        }
    };
    getStatus();
};

//payments
// export const getCheckoutSession = (type: SubscriptionType) => {
//     return api.get<{ url: string }>('/payments/checkout/session', {
//         params: {
//             type,
//         },
//     });
// };

export const getCheckoutSessionForPriceId = (priceId: string) => {
    return api.get<{ url: string }>(`/prices/${priceId}/checkout`);
};

export const getCheckoutSessionForApiCredits = () => {
    return api.get<{ url: string }>(`/payments/api/checkout/session`);
};

export type ModifySubscriptionType = 'cancel' | 'edit-payment' | 'general';

export const getPortalSession = ({
    type,
}: {
    type: ModifySubscriptionType;
}) => {
    return api.get<{ url: string }>('/payments/portal/session', {
        params: {
            type,
        },
    });
};

export interface PlanData {
    priceId: string;
    name: string;
    amount: number;
    trialDays: number;
    interval: 'month' | 'year';
    currency: 'ron' | 'usd';
}

export interface SeatInvite {
    id: string;
    invitedBy: string;
    plan: Plan;
    status: SeatStatus;
}

export interface Plans {
    anual: number;
    monthly: number;
    trialDays?: number;
    plans: PlanData[];
    seat?: SeatInvite;
}

export const getPlans = () => {
    return api.get<Plans>('/payments/plans');
};

export const getSignUrlForShortUrlId = (id: string) => {
    return api.get<{ signUrl: string }>(`/shorturls/${id}`);
};

export const getDocumentUrlForShortUrlId = (id: string) => {
    return api.get<{ downloadDocUrl: string }>(`/shorturls/${id}/documentUrl`);
};

// export const getPackageConsumption = (packageId: string) => {
//     return api.get<{ requestsCount: number }>(`/requests/${packageId}/count`);
// };

export const getWebhooks = () => {
    return api.get<{
        webhooks: Webhook[];
        events: string[];
        eventsSampleData: { [key: string]: object };
    }>(`/webhooks`);
};

export const saveApiConfig = ({
    webhooks,
    notificationEmail,
    autoRefillEnabled,
    autoRefillThreshold,
    autoRefillQuantity,
}: {
    webhooks: Partial<Webhook>[];
    notificationEmail: string | null;
    autoRefillEnabled: boolean;
    autoRefillThreshold: number | null;
    autoRefillQuantity: number | null;
}) => {
    return api.put<{
        webhooks: Webhook[];
        apiPackage: {
            id: string;
            notificationEmail: string | null;
            autoRefillEnabled: boolean;
            autoRefillThreshold: number | null;
            autoRefillQuantity: number | null;
        };
    }>(`/api-config`, {
        webhooks,
        notificationEmail,
        autoRefillEnabled,
        autoRefillThreshold,
        autoRefillQuantity,
    });
};

export const saveAdditionalFile = (requestId: string, fileUrl: string) => {
    return api.put('/requests/additionalFiles', { requestId, fileUrl });
};

export const deleteAdditionalFile = (requestId: string, fileId: string) => {
    return api.delete(`/requests/${requestId}/additionalFiles/${fileId}`);
};

export const remindRecipient = (recipientId: string) => {
    return api.put(`/recipients/${recipientId}/remind`);
};

export const canRemindRecipientOrThrow = (recipientId: string) => {
    return api.get(`/recipients/${recipientId}/canRemind`);
};

export const canRemindRequests = (ids: string[]) => {
    return api.put<{ id: string; canRemind: boolean }[]>(
        `/requests/canRemindRequests`,
        { ids }
    );
};

export const remindRecipients = (requestId: string) => {
    return api.put(`/requests/${requestId}/remindRecipients`);
};

export const createBulkRequest = (data: any) => {
    return api.post(`/bulkRequests`, data);
};

export const getBulkRequests = ({
    cursor,
    limit,
}: {
    cursor?: string;
    limit?: number;
}) => {
    return api.get<BulkRequest[]>('/bulkRequests', {
        params: {
            cursor,
            limit,
        },
    });
};

export const getBulkRequestById = (requestId: string) => {
    return api.get<BulkRequest>(`/bulkRequests/${requestId}`);
};

export const listenToBulkRequestUpdates = ({
    requestId,
    setUpdatedStatus,
    setUpdatedSentRequestsCount,
}: {
    requestId: string;
    setUpdatedStatus: Dispatch<SetStateAction<BulkRequestStatus | undefined>>;
    setUpdatedSentRequestsCount: Dispatch<SetStateAction<number | undefined>>;
}) => {
    let stopListening = false;

    const updateState = async () => {
        if (stopListening) return;
        try {
            const { data } = await getBulkRequestById(requestId);
            const { status, sentRequestsCount } = data;
            setUpdatedSentRequestsCount(sentRequestsCount);
            if (status !== 'SENT') {
                await wait(1000);
                updateState();
            } else {
                setUpdatedStatus('SENT');
            }
        } catch (error) {
            //do nothing?
        }
    };
    updateState();

    const stopListener = () => {
        stopListening = true;
    };

    return stopListener;
};

export const getFieldsFromTags = (fileName: string) => {
    return api.get<{ fields: Field[]; recipients: RecipientTag[] }>(
        `/fields/${fileName}/fromTags`
    );
};

// Uploads (and converts to PDF) an extra document of a request
export const uploadDocument = (
    file: File,
    progressUpdateFn?: (progress: number) => void
) => {
    return api.postForm<{
        id: string;
        docName: string;
        path: string;
        fileName: string;
    }>('/documents', [file], {
        onUploadProgress: (progressEvent) => {
            const progress = progressEvent.loaded / progressEvent.total;
            progressUpdateFn?.(progress);
        },
        timeout: 120000,
    });
};

export const uploadFile = (
    file: File,
    progressUpdateFn?: (progress: number) => void
) => {
    return api.postForm<{ path: string; fileName: string }>('/files', [file], {
        onUploadProgress: (progressEvent) => {
            const progress = progressEvent.loaded / progressEvent.total;
            progressUpdateFn?.(progress);
        },
        timeout: 60000,
    });
};

export const uploadFileAttachment = ({
    file,
    recipientId,
    requestId,
    progressUpdateFn,
}: {
    file: File;
    recipientId: string;
    requestId: string;
    progressUpdateFn?: (progress: number) => void;
}) => {
    return api.postForm<{ path: string; fileName: string }>(
        `/recipients/${recipientId}/attachments`,
        [file],
        {
            params: {
                requestId,
            },
            onUploadProgress: (progressEvent) => {
                const progress = progressEvent.loaded / progressEvent.total;
                progressUpdateFn?.(progress);
            },
        }
    );
};

export const uploadFileAttachmentNoAuth = ({
    file,
    token,
    progressUpdateFn,
}: {
    file: File;
    token: string;
    progressUpdateFn?: (progress: number) => void;
}) => {
    return api.postForm<{ path: string; fileName: string }>(
        `/noAuth/recipients/attachments`,
        [file],
        {
            params: {
                token,
            },
            onUploadProgress: (progressEvent) => {
                const progress = progressEvent.loaded / progressEvent.total;
                progressUpdateFn?.(progress);
            },
        }
    );
};

export const noop = () => {
    return api.get('/noop');
};

export const refreshAuthTokenIfExpired = () => {
    return noop();
};

const getDownloadBlob = async (
    url: string,
    context: Record<string, unknown>,
    params?: Record<string, unknown>
) => {
    try {
        const res = await api.get<Blob>(url, {
            responseType: 'blob',
            params,
            // large and bulk downloads can exceed the client's default timeout
            timeout: 0,
        });
        const contentDisposition = res.headers['content-disposition'];
        return {
            blob: res.data,
            contentDisposition:
                typeof contentDisposition === 'string'
                    ? contentDisposition
                    : null,
        };
    } catch (error) {
        return handleDownloadError(error, context);
    }
};

const fetchFileAsBlob = async (
    fileName: string,
    { templateId }: { templateId?: string } = {}
) => {
    const { blob } = await getDownloadBlob(
        `/files/${fileName}`,
        { fileName, templateId, method: 'fetchFileAsBlob' },
        { templateId }
    );
    return { blob, fileName };
};

interface FetchOptions {
    isBulk?: boolean;
    shouldAppendSignedSuffix?: boolean;
    documentId?: string | null;
}

const fetchDocument = async (
    requestId: string,
    options?: FetchOptions & { isDownload?: boolean }
) => {
    const { blob, contentDisposition } = await getDownloadBlob(
        `/requests/${requestId}/document`,
        { requestId, method: 'fetchDocument' },
        {
            ...(options?.isBulk && { isBulk: true }),
            ...(options?.isDownload && { download: true }),
            ...(options?.documentId && { documentId: options.documentId }),
        }
    );
    return { blob, fileName: extractFileNameFromHeader(contentDisposition) };
};

interface NoAuthOptions {
    isCompleted?: boolean;
    documentId?: string | null;
}

const fetchDocumentNoAuth = async (token: string, options?: NoAuthOptions) => {
    const params = new URLSearchParams({
        token,
        ...(options?.documentId && { documentId: options.documentId }),
    });

    try {
        const res = await fetch(
            `${API_BASE_URL}/noAuth/documents${
                options?.isCompleted ? '/completed' : ''
            }?${params.toString()}`,
            { headers: { 'X-Client-Version': String(CLIENT_VERSION) } }
        );

        if (res.ok) {
            const contentDispositionHeader = res.headers.get(
                'Content-Disposition'
            );
            const fileName = extractFileNameFromHeader(
                contentDispositionHeader
            );

            const blob = await res.blob();
            return { blob, fileName };
        } else {
            await handleFetchError(res, {
                token,
                method: 'fetchDocumentNoAuth',
            });
            throw new Error(); // just to fix TS
        }
    } catch (error) {
        throw new Error('Download error.');
    }
};

export const downloadDocument = async (
    requestId: string,
    options?: FetchOptions
) => {
    const { blob, fileName } = await fetchDocument(requestId, {
        ...options,
        isDownload: true,
    });
    const downloadName = options?.shouldAppendSignedSuffix
        ? appendSignedSuffix(fileName)
        : fileName;
    saveAs(blob, downloadName);
};

// Downloads the main document and every additional document as separate files
export const downloadRequestDocuments = async (
    request: Pick<Request, 'id' | 'documents'>,
    options?: FetchOptions
) => {
    const documentIds = [
        null,
        ...(request.documents ?? []).map(({ id }) => id),
    ];
    for (const documentId of documentIds) {
        await downloadDocument(request.id, { ...options, documentId });
    }
};

export const getDocumentAsFile = async (
    requestId: string,
    documentId?: string | null
) => {
    const { blob, fileName } = await fetchDocument(requestId, { documentId });
    return new File([blob], fileName);
};

export const getDocumentToSignAsFile = async (
    requestId: string,
    recipientId: string,
    sessionId: string,
    documentId?: string | null
) => {
    const response = await api.get(
        `/requests/${requestId}/recipients/${recipientId}/document-to-sign`,
        {
            responseType: 'blob',
            headers: { 'X-Session-Id': sessionId },
            ...(documentId && { params: { documentId } }),
        }
    );

    const contentDisposition = response.headers['content-disposition'];
    const fileName = extractFileNameFromHeader(contentDisposition);
    const blob = new Blob([response.data]);
    return new File([blob], fileName);
};

export const getDocumentToSignAsFileNoAuth = async (
    token: string,
    sessionId: string,
    documentId?: string | null
) => {
    const response = await api.get(`/noAuth/documents/to-sign`, {
        responseType: 'blob',
        params: { token, ...(documentId && { documentId }) },
        headers: { 'X-Session-Id': sessionId },
    });

    const contentDisposition = response.headers['content-disposition'];
    const fileName = extractFileNameFromHeader(contentDisposition);
    const blob = new Blob([response.data]);
    return new File([blob], fileName);
};

export const downloadCompletedDocumentNoAuth = async (
    token: string,
    documentId?: string | null
) => {
    const { blob, fileName } = await fetchDocumentNoAuth(token, {
        isCompleted: true,
        documentId,
    });
    saveAs(blob, appendSignedSuffix(fileName));
};

export const downloadCertificateNoAuth = async (token: string) => {
    try {
        const res = await fetch(
            `${API_BASE_URL}/noAuth/certificates?token=${token}`,
            { headers: { 'X-Client-Version': String(CLIENT_VERSION) } }
        );

        if (res.ok) {
            const contentDispositionHeader = res.headers.get(
                'Content-Disposition'
            );
            const fileName = extractFilenameForCertificate(
                contentDispositionHeader!
            );
            const blob = await res.blob();
            saveAs(blob, fileName);
        } else {
            const error = await res.json();
            throw new Error(error.message || 'Download error.');
        }
    } catch (error) {
        throw error;
    }
};

export const getFileAsFile = async (
    file: string,
    { templateId }: { templateId?: string } = {}
) => {
    const { blob, fileName } = await fetchFileAsBlob(file, { templateId });
    return new File([blob], fileName);
};

export const getCompletedDocumentNameNoAuth = (token: string) => {
    return api.get<{
        docName: string;
        brand: Brand;
        documents?: RequestDocumentInfo[];
    }>('/noAuth/documents/completed/name', {
        params: { token },
    });
};

export const getRequestDetails = (requestId: string) => {
    return api.get<Partial<Request>>(`/requests/${requestId}/details`);
};

const fetchFileAttachment = async (id: string) => {
    const { blob, contentDisposition } = await getDownloadBlob(
        `/requests/attachments/${id}`,
        { id, method: 'fetchFileAttachment' }
    );
    return { blob, fileName: extractFileNameFromHeader(contentDisposition) };
};

export const downloadFileAttachment = async (id: string) => {
    const { blob, fileName } = await fetchFileAttachment(id);
    saveAs(blob, fileName);
};

export const enableApi = () => {
    return api.put('/users/enableApi');
};

export const revokeApiKey = () => {
    return api.delete('/users/api-key');
};

export const regenerateApiKey = () => {
    return api.post<{ apiKey: string }>('/users/api-key/regenerate');
};

export const enableApi_v2 = () => {
    return api.put('/users/enableApi_v2');
};

export const generateApiKey_v2 = (name: string) => {
    return api.post<{ apiKey: string }>('/users/api-key/generate_v2', { name });
};

export const revokeApiKey_v2 = (id: string) => {
    return api.delete(`/users/api-key/${id}/revoke_v2`);
};

export interface ApiPackageDataRes {
    consumption: number;
    startDate: string;
    endDate: string;
    numOfRequests: number;
    notificationEmail: string | null;
    autoRefillEnabled: boolean;
    autoRefillThreshold: number | null;
    autoRefillQuantity: number | null;
    apiKeys: {
        id: string;
        type: string;
        name: string;
        lastChars: string;
        createdAt: string;
    }[];
}

export const getApiPackageData = () => {
    return api.get<ApiPackageDataRes>('/users/apiPackageData');
};

export const getApiDevPackageData = () => {
    return api.get<ApiPackageDataRes>('/users/apiDevPackageData');
};

export interface MonthlyApiConsumptionItem {
    month: number;
    year: number;
    count: number;
}

export interface MonthlyApiConsumptionRes {
    monthly: MonthlyApiConsumptionItem[];
    total: number;
}

export const getApiMonthlyConsumption = () => {
    return api.get<MonthlyApiConsumptionRes>(
        '/users/apiPackageData/monthlyConsumption'
    );
};

export const resetPassword = (email: string) => {
    return api.put(`/users/${email}/resetPassword`);
};

export const getSeats = () => {
    return api.get<Seat[]>(`/seats`);
};

export const addSeat = (email: string) => {
    return api.put(`/seats/${email}`);
};

export const deleteSeat = (email: string) => {
    return api.delete(`/seats/${email}`);
};

export const sendSeatInviteResponse = (accept: boolean) => {
    return api.put('/seats/inviteResponse', undefined, {
        params: {
            accept,
        },
    });
};

export const getTeams = () => {
    return api.get<Team[]>('/teams');
};

export interface MemberTeam {
    id: string;
    name: string;
    notifyOnNewRequest: boolean;
    users: {
        email: string;
        name: string;
    }[];
}

export const getMemberTeams = () => {
    return api.get<MemberTeam[]>('/teams/member');
};

export const updateTeamNotifications = (teamId: string, enabled: boolean) => {
    return api.put(`/teams/${teamId}/notifications`, { enabled });
};

export const createTeam = (team: Team) => {
    return api.post<Team>('/teams', team);
};

export const updateTeam = (team: Team) => {
    return api.put<Team>(`/teams/${team.id}`, team);
};

export const deleteTeam = (id: string) => {
    return api.delete(`/teams/${id}`);
};

export const super_getDocUrlAsFile = async (
    requestId: string,
    documentId?: string | null
) => {
    const { blob } = await getDownloadBlob(
        `/super/requests/${requestId}/document`,
        { requestId, method: 'super_getDocUrlAsFile' },
        documentId ? { documentId } : undefined
    );
    return new File([blob], 'document.pdf');
};

export const getDistinctRecipients = () => {
    return api.get<{ emails: string[] }>('/recipients/distinct');
};

interface RejectParams {
    reason: string;
    requestId: string;
    recipientId: string;
    sessionId?: string;
    docHash: string;
}

export const rejectSignRequest = ({
    requestId,
    reason,
    recipientId,
    sessionId,
    docHash,
}: RejectParams) => {
    return api.put(
        `/requests/${requestId}/reject`,
        { reason, docHash, recipientId },
        {
            headers: sessionId ? { 'X-Session-Id': sessionId } : undefined,
        }
    );
};

interface RejectNoAuthParams {
    reason: string;
    token: string;
    sessionId?: string;
    docHash: string;
}

export const rejectSignRequestNoAuth = ({
    reason,
    token,
    sessionId,
    docHash,
}: RejectNoAuthParams) => {
    return api.put(
        `/requests/reject/noauth`,
        { reason, docHash },
        {
            params: { token },
            headers: sessionId ? { 'X-Session-Id': sessionId } : undefined,
        }
    );
};

export const disableMfa = () => {
    return api.delete('/mfa');
};

export const super_disableUserMfa = (userId: string) => {
    return api.delete(`/super/mfa/${userId}`);
};

export const super_requestEmailChange = (
    oldEmail: string,
    newEmail: string
) => {
    return api.post(`/super/requestEmailChange`, { oldEmail, newEmail });
};

export interface GdprErasureReport {
    userId: string;
    accountDeletedNow: boolean;
    anonymizedRequestIds: string[];
    alreadyAnonymizedRequestIds: string[];
    filesReplaced: number;
    keptRequests: {
        requestId: string;
        reason: 'OTHER_RECIPIENTS' | 'HAS_CC' | 'PREVIOUS_EMAIL_RECIPIENTS';
    }[];
    signedForOthers: {
        requestId: string;
        senderId: string;
        senderEmail: string;
    }[];
    manualSteps: string[];
}

export const super_gdprErasure = (userId: string, prevEmail?: string) => {
    return api.post<GdprErasureReport>(`/super/gdpr-erasure`, {
        userId,
        ...(prevEmail && { prevEmail }),
    });
};

export interface MonthlyPayoutsResponse {
    monthlyTotals: Record<string, number>;
    grandTotal: number;
}

export const super_getMonthlyPayouts = () => {
    return api.get<MonthlyPayoutsResponse>(`/super/payouts`);
};

const fetchCertificate = async (requestId: string) => {
    const { blob, contentDisposition } = await getDownloadBlob(
        `/certificates/${requestId}`,
        { requestId, method: 'fetchCertificate' }
    );
    return {
        blob,
        fileName: extractFilenameForCertificate(contentDisposition ?? ''),
    };
};

export const downloadCertificate = async (requestId: string) => {
    const { blob, fileName } = await fetchCertificate(requestId);
    saveAs(blob, fileName);
};

export const super_downloadCertificate = async (requestId: string) => {
    const { blob, contentDisposition } = await getDownloadBlob(
        `/super/requests/${requestId}/certificate`,
        { requestId, method: 'super_downloadCertificate' }
    );
    saveAs(blob, extractFilenameForCertificate(contentDisposition ?? ''));
};

export const getBrand = () => {
    return api.get<Brand>('/brand');
};

export const createBrand = (brand: Brand) => {
    return api.post<Brand>('/brand', brand);
};

export const updateBrand = (brand: Brand) => {
    return api.put<Brand>('/brand', brand);
};

export const getPublicBrand = (slug: string) => {
    return api.get<Brand>(`/brand/${slug}`);
};

export const sendCode = ({
    requestId,
    recipientId,
    phoneNumber,
    sessionId,
    docHash,
    channel,
}: any) => {
    return api.post(
        '/requests/otpVerifications/sendCode',
        {
            requestId,
            recipientId,
            phoneNumber,
            docHash,
            channel,
        },
        {
            headers: sessionId ? { 'X-Session-Id': sessionId } : undefined,
        }
    );
};

export const sendCode_NoAuth = ({
    token,
    phoneNumber,
    sessionId,
    docHash,
    channel,
}: any) => {
    return api.post(
        '/requests/otpVerifications/sendCode/noAuth',
        { phoneNumber, docHash, channel },
        {
            params: { token },
            headers: sessionId ? { 'X-Session-Id': sessionId } : undefined,
        }
    );
};

export const verifyCode = ({
    requestId,
    recipientId,
    userCode,
    sessionId,
    docHash,
}: any) => {
    return api.post(
        '/requests/otpVerifications/verifyCode',
        {
            requestId,
            recipientId,
            userCode,
            docHash,
        },
        {
            headers: sessionId ? { 'X-Session-Id': sessionId } : undefined,
        }
    );
};

export const verifyCode_NoAuth = ({
    token,
    userCode,
    sessionId,
    docHash,
}: any) => {
    return api.post(
        '/requests/otpVerifications/verifyCode/noAuth',
        { userCode, docHash },
        {
            params: { token },
            headers: sessionId ? { 'X-Session-Id': sessionId } : undefined,
        }
    );
};

const handleFetchError = async (
    res: Response,
    context: any
): Promise<never> => {
    const responseText = await res.text();

    const error = new Error(`Failed to fetch document.`);

    Sentry.captureException(error, {
        extra: {
            type: 'file-fetch-error',
            context: JSON.stringify(context),
            status: res.status,
            statusText: res.statusText,
            headers: Object.fromEntries(res.headers.entries()),
            responseBody: responseText,
        },
    });

    throw error;
};

const handleDownloadError = async (
    error: unknown,
    context: Record<string, unknown>
): Promise<never> => {
    const response = axios.isAxiosError(error) ? error.response : undefined;
    const responseBody =
        response?.data instanceof Blob ? await response.data.text() : undefined;

    Sentry.captureException(new Error('Failed to fetch document.'), {
        extra: {
            type: 'file-fetch-error',
            context: JSON.stringify(context),
            status: response?.status,
            statusText: response?.statusText,
            headers: response?.headers,
            responseBody,
            errorMessage: error instanceof Error ? error.message : undefined,
        },
    });

    throw new Error('Download error.');
};

export const super_remindRecipient = (recipientId: string) => {
    return api.get(`/super/remindRecipient/${recipientId}`);
};

export const super_resendCompleted = (recipientId: string) => {
    return api.get(`/super/resendCompletedDocument/${recipientId}`);
};

export const super_republishMessage = (recipientId: string) => {
    return api.post(`/super/republishMessage/${recipientId}`);
};

export const super_retryStuckDss = () => {
    return api.post<{ count: number }>(`/super/retryStuckDss`);
};

export const updateRecipient = ({
    id,
    emailOrPhone,
}: {
    id: string;
    emailOrPhone: string;
}) => {
    return api.put<Recipient>(`/recipients/${id}`, { emailOrPhone });
};

export interface UsageAnalytics {
    totalRequests: number;
    userMonthlyStats: {
        email: string;
        months: {
            month: string;
            count: number;
        }[];
    }[];
}

export const getUsageAnalyticsForPackageId = (packageId: string) => {
    return api.get<UsageAnalytics>(`/packages/${packageId}/analytics`);
};

export interface Reports {
    from: string;
    to: string;
    bucket: 'day' | 'week' | 'month';
    totals: {
        sent: number;
        completed: number;
        inProgress: number;
        rejected: number;
        canceled: number;
        completionRate: number;
    };
    timeseries: { date: string; sent: number; completed: number }[];
    completionTime: {
        count: number;
        avgSeconds: number | null;
        medianSeconds: number | null;
    };
    signers: {
        total: number;
        signed: number;
        pending: number;
        rejected: number;
    };
    bySource: { WEB: number; BULK: number; API: number };
    topRecipients: {
        identifier: string;
        name: string | null;
        requested: number;
        signed: number;
    }[];
    topTags: { name: string; count: number }[];
}

const getTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getReports = ({ from, to }: { from: string; to: string }) => {
    return api.get<Reports>('/reports', {
        params: { from, to, tz: getTimeZone() },
    });
};

export const downloadReportsCsv = async ({
    from,
    to,
    lang,
}: {
    from: string;
    to: string;
    lang: 'ro' | 'en';
}) => {
    const response = await api.get<Blob>('/reports/csv', {
        params: { from, to, lang, tz: getTimeZone() },
        responseType: 'blob',
    });
    const fileName = extractFileNameFromHeader(
        response.headers['content-disposition']
    );

    saveAs(response.data, fileName);
};

export const acceptConsentNoAuth = ({
    token,
    requestId,
    sessionId,
    docHash,
}: {
    token: string;
    requestId: string;
    sessionId: string;
    docHash?: string;
}) => {
    return api.post(
        `/requests/${requestId}/sign/consent/noauth`,
        docHash ? { docHash } : {},
        {
            params: { token },
            headers: { 'X-Session-Id': sessionId },
        }
    );
};

// email text templates
export const getEmailTextTemplates = (type: EmailTextTemplateType) => {
    return api.get<EmailTextTemplate[]>('/email-templates', {
        params: { type },
    });
};

export const createEmailTextTemplate = ({
    content,
    type,
    shared,
}: {
    content: string;
    type: EmailTextTemplateType;
    shared: boolean;
}) => {
    return api.post<EmailTextTemplate>('/email-templates', {
        content,
        type,
        shared,
    });
};

export const deleteEmailTextTemplate = (id: string) => {
    return api.delete(`/email-templates/${id}`);
};
